// @ts-nocheck
import React, { useState } from 'react';
import { Input } from '@frontend/shadcn/components/ui/input';
import {
  GripVerticalIcon,
  BellIcon as RegularBellIcon,
  XmarkIcon,
  PlusIcon,
  CircleStopIcon as RegularCircleStopIcon,
  CheckIcon
} from '@revfluence/fresh-icons/regular/esm';
import {
  BellIcon as SolidBellIcon,
  CircleStopIcon as SolidCircleStopIcon,
} from '@revfluence/fresh-icons/solid/esm';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { cn } from '@frontend/shadcn/lib/utils';
import { useCustomStages } from '../context/CustomStagesContext';

// Keep these for consistent UI
const STATUS_COLORS = {
  'Waiting for Content': 'bg-sky-200',
  'Ad Ready': 'bg-emerald-100',
  'Send Newsletter': 'bg-amber-100',
  'Sent': 'bg-emerald-100',
  'Info Needed': 'bg-sky-200',
  'Waiting for Email': 'bg-sky-200',
  'Send Email': 'bg-amber-100',
  'Pending Submission': 'bg-sky-200',
  'Review Needed': 'bg-amber-100',
  'Done': 'bg-emerald-100',
  'Rejected': 'bg-red-200',
  'Wait for Review': 'bg-sky-200',
  'Review Proposal': 'bg-amber-100',
  'Approved': 'bg-emerald-100',
};

interface NewStatus {
  name: string;
  isNew?: boolean;
}

interface Props {
  statuses: Array<{
    id: number;
    name: string;
    priority: number;
    metadata?: {
      isFinalStatus?: boolean;
      enableNotification?: boolean;
    };
  }>;
  setStatuses: (statuses: Array<{
    id: number;
    name: string;
    priority: number;
    metadata?: {
      isFinalStatus?: boolean;
      enableNotification?: boolean;
    };
  }>) => void;
}

export const StatusSection: React.FC<Props> = ({ statuses, setStatuses }) => {
  const { selectedStage, updateStage, isNewStage } = useCustomStages();
  const [newStatus, setNewStatus] = useState<NewStatus | null>(null);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(selectedStage.statuses);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update priorities based on new order
    const updatedStatuses = items.map((status, index) => ({
      ...status,
      priority: index + 1
    }));

    updateStage(selectedStage.id, { statuses: updatedStatuses });
  };

  const handleDragEndNewStatus = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(statuses);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setStatuses(items);
  };

  const handleDeleteStatus = (statusId: number) => {
    if (isNewStage) {
      setStatuses(statuses.filter((status) => status.id !== statusId));
    } else {
      const updatedStatuses = selectedStage.statuses.filter((status) => status.id !== statusId);
      updateStage(selectedStage.id, { statuses: updatedStatuses });
    }
  };

  const handleToggleFinalStatus = (statusId: number) => {
    if (isNewStage) {
      setStatuses(statuses.map((status) => {
        if (status.id === statusId) {
          return {
            ...status,
            metadata: {
              ...status.metadata,
              isFinalStatus: !(status.metadata?.isFinalStatus ?? false)
            }
          };
        }
        return status;
      }));
    } else {
      const updatedStatuses = selectedStage.statuses.map((status) => {
        if (status.id === statusId) {
          return {
            ...status,
            metadata: {
              ...status.metadata,
              isFinalStatus: !(status.metadata?.isFinalStatus ?? false)
            }
          };
        }
        return status;
      });
      updateStage(selectedStage.id, { statuses: updatedStatuses });
    }
  };

  const handleToggleNotification = (statusId: number) => {
    if (isNewStage) {
      setStatuses(statuses.map((status) => {
        if (status.id === statusId) {
          return {
            ...status,
            metadata: {
              ...status.metadata,
              enableNotification: !(status.metadata?.enableNotification ?? false)
            }
          };
        }
        return status;
      }));
    } else {
      const updatedStatuses = selectedStage.statuses.map((status) => {
        if (status.id === statusId) {
          return {
            ...status,
            metadata: {
              ...status.metadata,
              enableNotification: !(status.metadata?.enableNotification ?? false)
            }
          };
        }
        return status;
      });
      updateStage(selectedStage.id, { statuses: updatedStatuses });
    }
  };

  const handleAddStatus = () => {
    setNewStatus({ name: '', isNew: true });
  };

  const handleSaveNewStatus = () => {
    if (!newStatus?.name.trim()) return;

    const newStatusObj = {
      id: Date.now(),
      name: newStatus.name,
      priority: (isNewStage ? statuses : selectedStage?.statuses || []).length + 1,
      metadata: {
        isFinalStatus: false,
        enableNotification: false
      }
    };

    if (isNewStage) {
      setStatuses([...statuses, newStatusObj]);
    } else {
      const updatedStatuses = [...(selectedStage?.statuses || []), newStatusObj];
      updateStage(selectedStage.id, { statuses: updatedStatuses });
    }
    setNewStatus(null);
  };

  const handleCancelNewStatus = () => {
    setNewStatus(null);
  };

  const renderStatusList = () => (
    <DragDropContext onDragEnd={isNewStage ? handleDragEndNewStatus : handleDragEnd}>
      <Droppable droppableId="statuses">
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef} className="flex flex-col gap-2">
            {(isNewStage ? statuses : selectedStage?.statuses || []).map((status, index) => (
              <Draggable
                key={status.id.toString()}
                draggableId={status.id.toString()}
                index={index}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    className={cn(
                      'p-2 rounded-xl outline outline-1 outline-offset-[-1px] outline-gray-200',
                      'inline-flex items-center gap-2 bg-white transition-all duration-200',
                      snapshot.isDragging && 'shadow-lg'
                    )}
                  >
                    <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing">
                      <GripVerticalIcon className="w-3 h-3 text-gray-300" />
                    </div>
                    <div className="flex-1 flex items-center gap-2">
                      <div className="p-1 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200">
                        <div className={cn(
                          'w-6 h-6 rounded-md border border-gray-200',
                          STATUS_COLORS[status.name] || 'bg-sky-200'
                        )}
                        />
                      </div>
                      <span className="flex-1 text-sm font-medium text-gray-800">{status.name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        className="w-8 h-8 rounded-lg flex justify-center items-center hover:bg-gray-50 transition-colors"
                        onClick={() => handleToggleFinalStatus(status.id)}
                        title={status.metadata?.isFinalStatus ? 'Remove final status' : 'Set as final status'}
                      >
                        <div className="w-3.5 h-3.5 inline-flex justify-center items-center">
                          {status.metadata?.isFinalStatus ? (
                            <SolidCircleStopIcon className="text-gray-800" />
                          ) : (
                            <RegularCircleStopIcon className="text-gray-400" />
                          )}
                        </div>
                      </button>
                      <button
                        className="w-8 h-8 rounded-lg flex justify-center items-center hover:bg-gray-50 transition-colors"
                        onClick={() => handleToggleNotification(status.id)}
                        title={status.metadata?.enableNotification ? 'Disable notifications' : 'Enable notifications'}
                      >
                        <div className="w-3.5 h-3.5 inline-flex justify-center items-center">
                          {status.metadata?.enableNotification ? (
                            <SolidBellIcon className="text-gray-800" />
                          ) : (
                            <RegularBellIcon className="text-gray-400" />
                          )}
                        </div>
                      </button>
                      <button
                        className="w-8 h-8 rounded-lg flex justify-center items-center hover:bg-gray-50 transition-colors"
                        onClick={() => handleDeleteStatus(status.id)}
                        title="Delete status"
                      >
                        <XmarkIcon className="w-3.5 h-3.5 text-gray-800" />
                      </button>
                    </div>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between items-start">
        <span className="text-sm text-gray-500">Use statuses to mark and track creators within this custom stage.</span>
        <button
          className="h-6 rounded-lg flex items-center gap-2 hover:bg-gray-50 px-2 transition-colors"
          onClick={handleAddStatus}
          disabled={!!newStatus}
        >
          <PlusIcon className="w-3.5 h-3.5 text-gray-800" />
          <span className="text-gray-800 text-sm font-medium">Add Status</span>
        </button>
      </div>

      {/* Status List */}
      {renderStatusList()}

      {/* New status input */}
      {newStatus && (
        <div className="p-2 rounded-xl outline outline-1 outline-offset-[-1px] outline-gray-200 inline-flex items-center gap-2 bg-white">
          <GripVerticalIcon className="w-3 h-3 text-gray-300" />
          <div className="flex-1 flex items-center gap-2">
            <div className="p-1 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200">
              <div className="w-6 h-6 rounded-md border border-gray-200 bg-sky-200" />
            </div>
            <Input
              value={newStatus.name}
              onChange={(e) => setNewStatus({ ...newStatus, name: e.target.value })}
              className="flex-1 h-8 px-3 py-1 text-sm"
              placeholder="Enter status name"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSaveNewStatus();
                } else if (e.key === 'Escape') {
                  handleCancelNewStatus();
                }
              }}
            />
          </div>
          <div className="flex items-center gap-1">
            <button
              className="w-8 h-8 rounded-lg flex justify-center items-center hover:bg-gray-50 transition-colors"
              onClick={handleCancelNewStatus}
              title="Cancel"
            >
              <XmarkIcon className="w-3.5 h-3.5 text-gray-800" />
            </button>
            <button
              className="w-8 h-8 rounded-lg flex justify-center items-center hover:bg-gray-50 transition-colors"
              onClick={handleSaveNewStatus}
              title="Save"
            >
              <CheckIcon className="w-3.5 h-3.5 text-gray-800" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
